import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productAPI, categoryAPI } from '../api';
import ProductCard from '../components/product/ProductCard';
import { SlidersHorizontal, Grid, List, X } from 'lucide-react';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'rating', label: 'Top Rated' },
  { value: 'popular', label: 'Most Popular' },
];

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    sort: searchParams.get('sort') || 'newest',
    rating: searchParams.get('rating') || '',
    page: 1,
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setFilters(f => ({
      ...f,
      search: searchParams.get('search') || '',
      category: searchParams.get('category') || '',
    }));
  }, [searchParams]);

  const { data, isLoading } = useQuery({
    queryKey: ['products', filters],
    queryFn: () => productAPI.getAll(filters).then(r => r.data),
    keepPreviousData: true,
  });

  const { data: catData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoryAPI.getAll().then(r => r.data),
  });

  const updateFilter = (key, value) => {
    setFilters(f => ({ ...f, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ search: '', category: '', minPrice: '', maxPrice: '', sort: 'newest', rating: '', page: 1 });
    setSearchParams({});
  };

  const hasFilters = filters.category || filters.minPrice || filters.maxPrice || filters.rating || filters.search;

  return (
    <div className="container" style={{ padding: '2rem 1.5rem', display: 'flex', gap: 24 }}>
      {/* Sidebar Filters */}
      <aside style={{
        width: 260, flexShrink: 0,
        display: window.innerWidth < 768 ? (showFilters ? 'block' : 'none') : 'block'
      }}>
        <div className="card" style={{ padding: '1.25rem', position: 'sticky', top: '120px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 700 }}>Filters</h3>
            {hasFilters && (
              <button onClick={clearFilters} style={{ fontSize: 12, color: '#f43397', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit' }}>
                Clear All
              </button>
            )}
          </div>

          {/* Categories */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</p>
            {catData?.categories?.map(cat => (
              <label key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                <input type="radio" name="category" checked={filters.category === cat.slug} onChange={() => updateFilter('category', cat.slug)} />
                <span style={{ fontSize: 14 }}>{cat.name}</span>
                <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto' }}>({cat.product_count})</span>
              </label>
            ))}
          </div>

          {/* Price Range */}
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 10, textTransform: 'uppercase' }}>Price Range</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="number" placeholder="Min ₹" value={filters.minPrice}
                onChange={e => updateFilter('minPrice', e.target.value)}
                style={{ width: '50%', padding: '8px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 13, outline: 'none' }} />
              <input type="number" placeholder="Max ₹" value={filters.maxPrice}
                onChange={e => updateFilter('maxPrice', e.target.value)}
                style={{ width: '50%', padding: '8px', border: '1.5px solid #e5e7eb', borderRadius: 6, fontSize: 13, outline: 'none' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              {[['Under ₹500', '', '500'], ['₹500-₹1000', '500', '1000'], ['₹1000-₹2000', '1000', '2000'], ['Above ₹2000', '2000', '']].map(([label, min, max]) => (
                <button key={label} onClick={() => { updateFilter('minPrice', min); updateFilter('maxPrice', max); }}
                  style={{ fontSize: 11, padding: '4px 8px', borderRadius: 20, border: '1px solid #e5e7eb', cursor: 'pointer', background: 'none', fontFamily: 'inherit', color: '#374151' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Rating */}
          <div>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 10, textTransform: 'uppercase' }}>Rating</p>
            {[4, 3, 2].map(r => (
              <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                <input type="radio" name="rating" checked={filters.rating === String(r)} onChange={() => updateFilter('rating', r)} />
                <span style={{ fontSize: 14 }}>{'⭐'.repeat(r)} & above</span>
              </label>
            ))}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700 }}>
              {filters.search ? `Results for "${filters.search}"` : filters.category ? filters.category.replace(/-/g, ' ').toUpperCase() : 'All Products'}
            </h1>
            {data?.pagination && (
              <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 4 }}>{data.pagination.total} products found</p>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <select value={filters.sort} onChange={e => updateFilter('sort', e.target.value)}
              style={{ padding: '8px 12px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', cursor: 'pointer', background: 'white' }}>
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        {/* Active filters */}
        {hasFilters && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {filters.category && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fce4f1', color: '#f43397', padding: '4px 12px', borderRadius: 20, fontSize: 13 }}>
                {filters.category} <X size={12} style={{ cursor: 'pointer' }} onClick={() => updateFilter('category', '')} />
              </span>
            )}
            {(filters.minPrice || filters.maxPrice) && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fce4f1', color: '#f43397', padding: '4px 12px', borderRadius: 20, fontSize: 13 }}>
                ₹{filters.minPrice || '0'} - ₹{filters.maxPrice || '∞'}
                <X size={12} style={{ cursor: 'pointer' }} onClick={() => { updateFilter('minPrice', ''); updateFilter('maxPrice', ''); }} />
              </span>
            )}
          </div>
        )}

        {/* Products grid */}
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '5rem' }}>
            <div className="spinner" />
          </div>
        ) : data?.products?.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '5rem 2rem' }}>
            <p style={{ fontSize: 48, marginBottom: 16 }}>🔍</p>
            <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>No products found</h3>
            <p style={{ color: '#9ca3af', marginBottom: 20 }}>Try adjusting your filters or search query</p>
            <button onClick={clearFilters} className="btn btn-primary">Clear Filters</button>
          </div>
        ) : (
          <>
            <div className="product-grid">
              {data?.products?.map(p => <ProductCard key={p.id} product={p} />)}
            </div>

            {/* Pagination */}
            {data?.pagination?.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 32 }}>
                {Array.from({ length: data.pagination.pages }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setFilters(f => ({ ...f, page: p }))}
                    style={{
                      width: 36, height: 36, borderRadius: 8, border: '1.5px solid',
                      borderColor: p === filters.page ? '#f43397' : '#e5e7eb',
                      background: p === filters.page ? '#f43397' : 'white',
                      color: p === filters.page ? 'white' : '#374151',
                      cursor: 'pointer', fontSize: 14, fontFamily: 'inherit'
                    }}>
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
